// Workflow Status Enums
export enum WorkflowStatus {
  PENDING = 'Pending',
  ASSIGNED = 'Assigned',
  IN_PRODUCTION = 'In Process',
  READY_FOR_TRIAL = 'Ready for Trial',
  COMPLETED = 'Completed'
}

// Work Styles by Category with Rates (from rate card)
export interface WorkStyle {
  id: string
  name: string
  category: WorkStyleCategory
  rate: number
  description?: string
}

export enum WorkStyleCategory {
  JACKETS = 'Jackets',
  SHIRTS = 'Shirts',
  TROUSERS = 'Trousers'
}

// Employee Types for Workflow Assignment
export enum EmployeeType {
  JACKET_MAKER = 'Jacket Maker',
  SHIRT_MAKER = 'Shirt Maker',
  TROUSER_MAKER = 'Trouser Maker',
  ALTERATION = 'Alteration'
}

// Workflow Assignment Interface
export interface WorkflowAssignment {
  id: string
  workflowIds: string[]
  orderId: string
  orderItemId: string
  garmentType: string
  quantity: number
  employeeId: string
  employeeName: string
  employeeType: EmployeeType
  workStyles: WorkStyleAssignment[]
  status: WorkflowStatus
  assignedAt?: Date
  completedAt?: Date
  totalPayment: number
  notes?: string
}

// Work Style Assignment Interface
export interface WorkStyleAssignment {
  id: string
  workStyleId: string
  workStyleName: string
  rate: number
  quantity: number
  totalAmount: number
  assignedAt: Date
}

// Employee Workload Interface
export interface EmployeeWorkload {
  employeeId: string
  employeeName: string
  employeeType: EmployeeType
  currentJobs: number
  totalAssigned: number
  completedJobs: number
  totalEarnings: number
  activeAssignments: WorkflowAssignment[]
}

// Workflow Statistics
export interface WorkflowStatistics {
  monthlyAssignments: number
  pendingAssignments: number
  monthlyCompletedJobs: number
  assignmentsUnderProcess: number
  totalPayments: number
}

// Workflow Filter Options
export interface WorkflowFilterOptions {
  status?: WorkflowStatus
  employeeType?: EmployeeType
  employeeId?: string
  orderId?: string
  dateFrom?: Date
  dateTo?: Date
  searchQuery?: string
}

// Monthly Report Interface
export interface MonthlyReport {
  month: string
  year: number
  employeeReports: EmployeeMonthlyReport[]
  totalPayments: number
  totalJobs: number
  averageJobsPerEmployee: number
}

// Employee Monthly Report
export interface EmployeeMonthlyReport {
  employeeId: string
  employeeName: string
  employeeType: EmployeeType
  totalJobs: number
  completedJobs: number
  totalPayments: number
  workStyleBreakdown: WorkStyleBreakdown[]
  assignments: WorkflowAssignment[]
}

// Work Style Breakdown for Reports
export interface WorkStyleBreakdown {
  workStyleName: string
  category: WorkStyleCategory
  quantity: number
  totalAmount: number
}

// Workflow Creation Request
export interface CreateWorkflowAssignmentRequest {
  orderId: string
  orderItemId: string
  garmentType: string
  quantity: number
  employeeId: string
  workStyleIds: string[]
  notes?: string
}

// Workflow Update Request
export interface UpdateWorkflowAssignmentRequest {
  id: string
  employeeId?: string
  workStyleIds?: string[]
  status?: WorkflowStatus
  notes?: string
}

// Workflow Search Result
export interface WorkflowSearchResult {
  id: string
  orderId: string
  customerName: string
  garmentType: string
  employeeName: string
  status: WorkflowStatus
  totalPayment: number
  assignedAt?: Date
}

// Workflow Pagination
export interface WorkflowPagination {
  page: number
  limit: number
  total: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

// Workflow List Response
export interface WorkflowListResponse {
  assignments: WorkflowAssignment[]
  pagination: WorkflowPagination
  statistics: WorkflowStatistics
}

// Predefined Work Styles with Rates (from rate card)
export const WORK_STYLES: WorkStyle[] = [
  // Jackets
  { id: 'js-1', name: 'SEMI-BESPOKE', category: WorkStyleCategory.JACKETS, rate: 175 },
  { id: 'js-2', name: 'BESPOKE JACKET', category: WorkStyleCategory.JACKETS, rate: 385 },
  { id: 'js-3', name: 'BESPOKE KACCHA', category: WorkStyleCategory.JACKETS, rate: 55 },
  { id: 'js-4', name: 'JACKET KACCHA', category: WorkStyleCategory.JACKETS, rate: 40 },
  { id: 'js-5', name: 'SHER/INDO', category: WorkStyleCategory.JACKETS, rate: 195 },
  { id: 'js-6', name: 'RS', category: WorkStyleCategory.JACKETS, rate: 90 },
  { id: 'js-7', name: 'HS', category: WorkStyleCategory.JACKETS, rate: 30 },
  { id: 'js-8', name: 'SAFARI', category: WorkStyleCategory.JACKETS, rate: 80 },
  { id: 'js-19', name: 'SAFARI TOP', category: WorkStyleCategory.JACKETS, rate: 85 },
  { id: 'js-20', name: 'BOMBER JACKET', category: WorkStyleCategory.JACKETS, rate: 140 },
  { id: 'js-9', name: 'BANDI', category: WorkStyleCategory.JACKETS, rate: 75 },
  { id: 'js-10', name: 'BANDI ALTER', category: WorkStyleCategory.JACKETS, rate: 30 },
  { id: 'js-11', name: 'VEST RETRAIL', category: WorkStyleCategory.JACKETS, rate: 15 },
  { id: 'js-12', name: 'VEST COAT', category: WorkStyleCategory.JACKETS, rate: 60 },
  { id: 'js-13', name: 'VEST KACCHA', category: WorkStyleCategory.JACKETS, rate: 10 },
  { id: 'js-14', name: 'RETRAIL', category: WorkStyleCategory.JACKETS, rate: 45 },
  { id: 'js-15', name: 'MIN ALTER', category: WorkStyleCategory.JACKETS, rate: 25 },
  { id: 'js-16', name: 'FULL ALTER', category: WorkStyleCategory.JACKETS, rate: 35 },
  { id: 'js-17', name: 'MID ALTER', category: WorkStyleCategory.JACKETS, rate: 25 },
  { id: 'js-18', name: 'EXTRA WORK', category: WorkStyleCategory.JACKETS, rate: 30 },
  { id: 'js-21', name: 'DOUBLE EXTRA WORK', category: WorkStyleCategory.JACKETS, rate: 30 },

  // Shirts
  { id: 'ss-16', name: 'BOMBER SHIRT', category: WorkStyleCategory.SHIRTS, rate: 140 },
  { id: 'ss-17', name: 'SAHARA TOP', category: WorkStyleCategory.SHIRTS, rate: 125 },
  { id: 'ss-18', name: 'SAFARI TOP', category: WorkStyleCategory.SHIRTS, rate: 85 },
  { id: 'ss-1', name: 'SHIRT', category: WorkStyleCategory.SHIRTS, rate: 32 },
  { id: 'ss-2', name: 'PAYJAMA', category: WorkStyleCategory.SHIRTS, rate: 22 },
  { id: 'ss-3', name: 'NG TOP', category: WorkStyleCategory.SHIRTS, rate: 45 },
  { id: 'ss-4', name: 'NG TOP L', category: WorkStyleCategory.SHIRTS, rate: 60 },
  { id: 'ss-5', name: 'KURTA', category: WorkStyleCategory.SHIRTS, rate: 38 },
  { id: 'ss-6', name: 'EMB TOP', category: WorkStyleCategory.SHIRTS, rate: 24 },
  { id: 'ss-7', name: 'ABAYA', category: WorkStyleCategory.SHIRTS, rate: 50 },
  { id: 'ss-8', name: 'KURTA WITH L', category: WorkStyleCategory.SHIRTS, rate: 50 },
  { id: 'ss-9', name: 'SAFARI', category: WorkStyleCategory.SHIRTS, rate: 85 },
  { id: 'ss-10', name: 'SALWAR CHUDIDAR', category: WorkStyleCategory.SHIRTS, rate: 22 },
  { id: 'ss-11', name: 'KALIDAR KURTA', category: WorkStyleCategory.SHIRTS, rate: 60 },
  { id: 'ss-12', name: 'KAFTAN', category: WorkStyleCategory.SHIRTS, rate: 56 },
  { id: 'ss-13', name: 'RS', category: WorkStyleCategory.SHIRTS, rate: 22 },
  { id: 'ss-14', name: 'ALTER', category: WorkStyleCategory.SHIRTS, rate: 10 },
  { id: 'ss-15', name: 'EXTRA WORK', category: WorkStyleCategory.SHIRTS, rate: 8 },
  { id: 'ss-19', name: 'DOUBLE EXTRA WORK', category: WorkStyleCategory.SHIRTS, rate: 8 },

  // Trousers
  { id: 'ts-11', name: 'BESPOKE TROUSER', category: WorkStyleCategory.TROUSERS, rate: 150 },
  { id: 'ts-1', name: 'TROUSER', category: WorkStyleCategory.TROUSERS, rate: 42 },
  { id: 'ts-2', name: 'FHL', category: WorkStyleCategory.TROUSERS, rate: 46 },
  { id: 'ts-3', name: 'NG TROUSER', category: WorkStyleCategory.TROUSERS, rate: 40 },
  { id: 'ts-4', name: 'T KACCHA', category: WorkStyleCategory.TROUSERS, rate: 14 },
  { id: 'ts-5', name: 'EMB TROUSER', category: WorkStyleCategory.TROUSERS, rate: 33 },
  { id: 'ts-6', name: 'FL TROUSER', category: WorkStyleCategory.TROUSERS, rate: 53 },
  { id: 'ts-7', name: 'RETRAIL', category: WorkStyleCategory.TROUSERS, rate: 6 },
  { id: 'ts-8', name: 'RS', category: WorkStyleCategory.TROUSERS, rate: 33 },
  { id: 'ts-9', name: 'ALTER', category: WorkStyleCategory.TROUSERS, rate: 10 },
  { id: 'ts-10', name: 'EXTRA WORK', category: WorkStyleCategory.TROUSERS, rate: 8 },
  { id: 'ts-12', name: 'DOUBLE EXTRA WORK', category: WorkStyleCategory.TROUSERS, rate: 8 }
]

// Helper functions
export const getWorkStylesByCategory = (category: WorkStyleCategory): WorkStyle[] => {
  return WORK_STYLES.filter(style => style.category === category)
}

export const getWorkStylesByGarmentType = (garmentType: string): WorkStyle[] => {
  switch (garmentType) {
    case 'Jacket & Vest':
    case 'Sherwani':
    case 'Alteration':
      return getWorkStylesByCategory(WorkStyleCategory.JACKETS)
    case 'Shirt':
    case 'Kurta':
    case 'Kaftan':
      return getWorkStylesByCategory(WorkStyleCategory.SHIRTS)
    case 'Trouser':
      return getWorkStylesByCategory(WorkStyleCategory.TROUSERS)
    default:
      return []
  }
}

export const getEmployeeTypeByGarmentType = (garmentType: string): EmployeeType => {
  switch (garmentType) {
    case 'Jacket & Vest':
    case 'Sherwani':
      return EmployeeType.JACKET_MAKER
    case 'Shirt':
    case 'Kurta':
    case 'Kaftan':
      return EmployeeType.SHIRT_MAKER
    case 'Trouser':
      return EmployeeType.TROUSER_MAKER
    case 'Alteration':
      return EmployeeType.ALTERATION
    default:
      return EmployeeType.JACKET_MAKER
  }
}
